import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { steamProfileUrl } from "../utils/steam";
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid
} from "recharts";

const API = process.env.REACT_APP_API_URL || "";

function StatBox({ value, label }) {
  return (
    <div className="stat-box">
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

function RatingChart({ matches }) {
  if (!matches || matches.length < 2) return null;
  const data = [...matches]
    .reverse()
    .map((m, i) => ({
      name: `#${i + 1}`,
      rating: parseFloat(m.hltv_rating) || 0,
      map: m.map,
    }));

  return (
    <div className="card">
      <h2>Rating Trend (last {data.length} matches)</h2>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2d3148" />
          <XAxis dataKey="name" tick={{ fill: "#9ca3af", fontSize: 11 }} />
          <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} domain={["auto", "auto"]} />
          <Tooltip
            contentStyle={{ background: "#1a1d26", border: "1px solid #2d3148", borderRadius: "6px" }}
            labelStyle={{ color: "#9ca3af" }}
            itemStyle={{ color: "#f0a500" }}
            formatter={(v) => [v.toFixed(3), "Rating"]}
          />
          <Line
            type="monotone"
            dataKey="rating"
            stroke="#f0a500"
            strokeWidth={2}
            dot={{ fill: "#f0a500", r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function PlayerProfile() {
  const { steamId } = useParams();
  const [player, setPlayer] = useState(null);
  const [career, setCareer] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/players/${steamId}`)
      .then((r) => {
        setPlayer(r.data.player);
        setCareer(r.data.career);
        setMatches(r.data.matches || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [steamId]);

  if (loading) return <p style={{ color: "#9ca3af" }}>Loading player...</p>;
  if (!player) return <p style={{ color: "#ef4444" }}>Player not found.</p>;

  const hsPercent =
    career?.total_kills > 0
      ? Math.round((career.total_headshots / career.total_kills) * 100)
      : 0;

  const kd =
    career?.total_deaths > 0
      ? (career.total_kills / career.total_deaths).toFixed(2)
      : career?.total_kills || 0;

  return (
    <div>
      <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ color: "#9ca3af", fontSize: "0.9rem" }}>
          ← Back to Leaderboard
        </Link>
        <Link to={`/compare?p1=${steamId}`}
          style={{ background: "#2d3148", color: "#e5e7eb", padding: "0.4rem 1rem", borderRadius: "6px", fontSize: "0.85rem", textDecoration: "none" }}>
          ⚔️ Compare
        </Link>
      </div>

      {/* Player Header */}
      <div className="card" style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
        {player.avatar_url ? (
          <img
            src={player.avatar_url}
            alt={player.name}
            style={{ width: "64px", height: "64px", borderRadius: "50%", border: "2px solid #f0a500" }}
          />
        ) : (
          <div
            style={{
              width: "64px", height: "64px", borderRadius: "50%",
              background: "#2d3148", display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: "1.8rem",
            }}
          >
            🎮
          </div>
        )}
        <div>
          <h1 style={{ marginBottom: "0.25rem" }}>{player.name}</h1>
          <div style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
            Steam ID: <span style={{ color: "#e5e7eb" }}>{player.steam_id}</span>
          </div>
          {player.discord_id && (
            <div style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
              Discord linked ✅
            </div>
          )}
        </div>
      </div>

      {/* Career Stats */}
      <div className="stat-grid">
        <StatBox value={career?.matches_played || 0} label="Matches" />
        <StatBox value={kd} label="K/D Ratio" />
        <StatBox value={career?.avg_rating || "0.000"} label="Avg Rating" />
        <StatBox value={career?.avg_adr || "0.0"} label="Avg ADR" />
        <StatBox value={`${career?.avg_kast || 0}%`} label="Avg KAST" />
        <StatBox value={`${hsPercent}%`} label="HS %" />
        <StatBox value={career?.total_kills || 0} label="Total Kills" />
        <StatBox value={career?.total_deaths || 0} label="Total Deaths" />
        <StatBox value={career?.total_5k || 0} label="Aces (5K)" />
        <StatBox value={career?.total_4k || 0} label="4K Rounds" />
        <StatBox value={career?.total_3k || 0} label="3K Rounds" />
        <StatBox value={`${career?.total_clutches_won || 0}/${career?.total_clutches || 0}`} label="Clutches" />
        <StatBox value={career?.total_ef || 0} label="Enemies Flashed" />
      </div>

      {/* Rating Chart */}
      <RatingChart matches={matches} />

      {/* Match History */}
      <div className="card">
        <h2>Recent Matches</h2>
        {matches.length === 0 ? (
          <p style={{ color: "#9ca3af" }}>No matches found.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Map</th>
                <th>Score</th>
                <th>K</th>
                <th>D</th>
                <th>A</th>
                <th>ADR</th>
                <th>Rating</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => (
                <tr key={m.match_id}>
                  <td style={{ fontWeight: "600" }}>{m.map}</td>
                  <td style={{ color: "#9ca3af" }}>
                    {m.team1_score} – {m.team2_score}
                  </td>
                  <td>{m.kills}</td>
                  <td>{m.deaths}</td>
                  <td>{m.assists}</td>
                  <td>{m.adr}</td>
                  <td
                    style={{
                      color:
                        m.hltv_rating >= 1.1
                          ? "#4ade80"
                          : m.hltv_rating >= 0.9
                          ? "#f0a500"
                          : "#ef4444",
                      fontWeight: "600",
                    }}
                  >
                    {m.hltv_rating}
                  </td>
                  <td style={{ color: "#9ca3af", fontSize: "0.8rem" }}>
                    {m.finished_at ? new Date(m.finished_at).toLocaleDateString() : "—"}
                  </td>
                  <td>
                    <Link
                      to={`/matches/${m.match_id}`}
                      style={{ color: "#5865f2", fontSize: "0.85rem" }}
                    >
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
